import React, { useState } from 'react';
import { navigate } from '@reach/router';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SyncIcon from '@mui/icons-material/Sync';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useRecentJobsContext } from '@particles';
import { routes } from '@particles';
import { DateTime } from 'luxon';

const JOB_TYPE_LABELS: Record<string, string> = {
  sync_contacts: 'Contact sync',
  artifact_performance_report: 'Performance report',
  artifact_cohort_report: 'Cohort report',
  artifact_mortgage_report: 'Mortgage report',
  artifact_negotiator_report: 'Negotiator report',
  delete_company: 'Delete company',
  signout_all_negotiator: 'Sign out all negotiator users'
};

const PEEK_LIMIT = 5;
const JUST_FINISHED_WINDOW_MS = 30 * 60 * 1000; // 30 minutes

const STATUS_LABELS: Record<string, string> = {
  pending: 'Queued',
  running: 'In Progress',
  completed: 'Done',
  failed: 'Failed',
  cancelled: 'Cancelled'
};

// Keys the server emits via BackgroundJob.currentStep — keep in sync with
// server/src/backgroundJobRunner.ts setStepInternal() call sites.
type StepKey = 'queued' | 'syncing_contacts' | 'generating_report' | 'done';

const STEP_LABEL_MAP: Record<StepKey, string> = {
  queued: 'Queued',
  syncing_contacts: 'Syncing contacts',
  generating_report: 'Generating',
  done: 'Done'
};

// A job's stepper always starts with Queued and ends with Done. The middle
// steps depend on what the runner will actually execute — driven off the
// original payload so a completed job's stepper still shows the sync phase
// when syncContactsFirst was requested.
function stepsForJob(job: { jobType: string; payload?: any }): StepKey[] {
  const includeSync =
    (job.jobType === 'artifact_performance_report' || job.jobType === 'artifact_cohort_report') &&
    job.payload?.syncContactsFirst === true;
  return includeSync
    ? ['queued', 'syncing_contacts', 'generating_report', 'done']
    : ['queued', 'generating_report', 'done'];
}

function activeStepIndex(
  job: { status: string; currentStep?: string | null },
  steps: StepKey[]
): number {
  if (job.status === 'pending') return 0;
  if (job.status === 'completed') return steps.length - 1;
  // running / failed — trust currentStep when the runner has emitted one
  if (job.currentStep) {
    const idx = steps.indexOf(job.currentStep as StepKey);
    if (idx >= 0) return idx;
  }
  // running with no currentStep set (older job types or the very first tick
  // before setStep fires): assume the primary work phase.
  const genIdx = steps.indexOf('generating_report');
  return genIdx >= 0 ? genIdx : 1;
}

const statusConfig: Record<string, { color: 'default' | 'primary' | 'success' | 'error' | 'warning'; icon: React.ReactNode }> = {
  pending: { color: 'warning', icon: <HourglassEmptyIcon fontSize="small" color="warning" /> },
  running: { color: 'primary', icon: <SyncIcon fontSize="small" color="primary" sx={{ animation: 'spin 1.5s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} /> },
  completed: { color: 'success', icon: <CheckCircleOutlineIcon fontSize="small" color="success" /> },
  failed: { color: 'error', icon: <ErrorOutlineIcon fontSize="small" color="error" /> },
  cancelled: { color: 'default', icon: <ErrorOutlineIcon fontSize="small" color="disabled" /> }
};

const MiniStepper: React.FC<{
  job: { status: string; jobType: string; currentStep?: string | null; payload?: any };
}> = ({ job }) => {
  const steps = stepsForJob(job);
  const activeStep = activeStepIndex(job, steps);
  const isFailed = job.status === 'failed';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
      {steps.map((stepKey, i) => {
        const stepLabel = STEP_LABEL_MAP[stepKey];
        const isActive = i === activeStep;
        const isCompleted = i < activeStep;
        const isFailedStep = isFailed && i === activeStep;

        let dotColor = 'grey.400';
        if (isFailedStep) dotColor = 'error.main';
        else if (isCompleted) dotColor = 'success.main';
        else if (isActive && job.status === 'pending') dotColor = 'warning.main';
        else if (isActive && job.status === 'running') dotColor = 'primary.main';
        else if (isActive && job.status === 'completed') dotColor = 'success.main';

        return (
          <React.Fragment key={stepKey}>
            {i > 0 && (
              <Box
                sx={{
                  width: 16,
                  height: 2,
                  bgcolor: isCompleted ? 'success.main' : 'grey.300',
                  borderRadius: 1
                }}
              />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Box
                sx={{
                  width: isActive ? 7 : 6,
                  height: isActive ? 7 : 6,
                  borderRadius: '50%',
                  bgcolor: dotColor,
                  transition: 'all 0.2s'
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  color: isActive || isCompleted ? 'text.primary' : 'text.disabled',
                  fontWeight: isActive ? 600 : 400,
                  lineHeight: 1
                }}
              >
                {stepLabel}
              </Typography>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

// While a job is running, prefer the step label ("Syncing contacts…") over the
// generic "In Progress" so the notification center reads like a live progress
// log rather than a spinner.
function chipLabelForJob(job: { status: string; currentStep?: string | null }): string {
  if (job.status === 'running' && job.currentStep) {
    const stepLabel = STEP_LABEL_MAP[job.currentStep as StepKey];
    if (stepLabel && stepLabel !== 'Queued' && stepLabel !== 'Done') {
      return `${stepLabel}…`;
    }
  }
  return STATUS_LABELS[job.status] || job.status;
}

const NotificationCenter: React.FC = () => {
  const { jobs } = useRecentJobsContext();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);
  const pendingCount = jobs.filter((j) => j.status === 'pending').length;
  const runningCount = jobs.filter((j) => j.status === 'running').length;
  const activeCount = pendingCount + runningCount;

  const now = Date.now();
  const isJustFinished = (job: typeof jobs[number]) => {
    if (job.status !== 'completed' && job.status !== 'failed') return false;
    if (!job.completedAt) return false;
    return now - new Date(job.completedAt).getTime() <= JUST_FINISHED_WINDOW_MS;
  };
  const isActive = (job: typeof jobs[number]) =>
    job.status === 'pending' || job.status === 'running';
  const displayJobs = jobs
    .filter((j) => isActive(j) || isJustFinished(j))
    .slice(0, PEEK_LIMIT);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewAll = () => {
    handleClose();
    navigate(routes.SA_Jobs);
  };

  return (
    <>
      <IconButton onClick={handleOpen} size="small" sx={{ color: '#555' }}>
        <Badge
          badgeContent={activeCount}
          color="error"
          max={9}
          invisible={activeCount === 0}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { width: 380, maxHeight: 480, display: 'flex', flexDirection: 'column' }
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Recent tasks
          </Typography>
          {activeCount > 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {[
                pendingCount > 0 && `${pendingCount} queued`,
                runningCount > 0 && `${runningCount} in progress`
              ].filter(Boolean).join(' · ')}
            </Typography>
          )}
        </Box>
        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {displayJobs.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No active tasks
              </Typography>
            </Box>
          ) : (
            displayJobs.map((job) => {
              const label = JOB_TYPE_LABELS[job.jobType] || job.jobType;
              const config = statusConfig[job.status] || statusConfig.pending;
              const shareUrl = job.result?.shareUrl;
              const createdAt = job.createdAt
                ? DateTime.fromISO(job.createdAt).toRelative()
                : null;

              return (
                <Box
                  key={job.jobId}
                  sx={{
                    px: 2,
                    py: 1.25,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:last-child': { borderBottom: 0 }
                  }}
                >
                  <Box sx={{ mt: 0.25, flexShrink: 0 }}>{config.icon}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {label}
                      </Typography>
                      <Chip
                        label={chipLabelForJob(job)}
                        color={config.color}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                    {createdAt && (
                      <Typography variant="caption" color="text.secondary">
                        {createdAt}
                      </Typography>
                    )}
                    {job.status === 'completed' && shareUrl && (
                      <Box sx={{ mt: 0.5 }}>
                        <Link
                          href={shareUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                          underline="hover"
                        >
                          Open report
                        </Link>
                      </Box>
                    )}
                    {job.status === 'completed' && !!job.result?.sendBlockers?.length && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.25 }}>
                        Not ready to send: {job.result.sendBlockers.length} check{job.result.sendBlockers.length === 1 ? '' : 's'} to clear
                      </Typography>
                    )}
                    {job.status === 'failed' && job.errorMessage && (
                      <Typography variant="caption" color="error" noWrap sx={{ display: 'block', mt: 0.25 }}>
                        {job.errorMessage}
                      </Typography>
                    )}
                    <MiniStepper job={job} />
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        <Divider />
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button size="small" onClick={handleViewAll} sx={{ textTransform: 'none' }}>
            Open Tasks
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationCenter;
